import React, { createContext, useContext, useState, useEffect } from 'react';
import { useWorkspace } from './WorkspaceContext';
import { useAuth } from './AuthContext';
import { 
  fetchTranslationKeys, 
  TranslationKey, 
  storeKeysInLocalStorage, 
  getKeysFromLocalStorage 
} from '../services/translationService';

interface TranslationKeyContextType {
  keys: TranslationKey[];
  loading: boolean;
  error: string | null;
  refreshKeys: () => Promise<void>;
}

const TranslationKeyContext = createContext<TranslationKeyContextType>({
  keys: [],
  loading: false,
  error: null,
  refreshKeys: async () => {},
});

export const useTranslationKeys = () => useContext(TranslationKeyContext);

export const TranslationKeyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedProject } = useWorkspace();
  const { user } = useAuth();
  const [keys, setKeys] = useState<TranslationKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadKeys = async () => {
    if (!selectedProject || !user) {
      setKeys([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Try to get keys from local storage first
      const cachedKeys = getKeysFromLocalStorage(user.id);
      
      // If we have cached keys, use them initially
      if (cachedKeys.length > 0) {
        setKeys(cachedKeys);
      }
      
      // Fetch fresh keys from the API
      const fetchedKeys = await fetchTranslationKeys(selectedProject.id);
      
      // Update state with fresh keys
      setKeys(fetchedKeys);
      
      // Store the fresh keys in local storage
      if (fetchedKeys.length > 0) {
        storeKeysInLocalStorage(user.id, fetchedKeys);
      }
    } catch (err) {
      console.error('Error loading translation keys:', err);
      setError('Failed to load translation keys. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load keys when the selected project changes
  useEffect(() => {
    loadKeys();
  }, [selectedProject, user]);

  return (
    <TranslationKeyContext.Provider
      value={{
        keys,
        loading,
        error,
        refreshKeys: loadKeys,
      }}
    >
      {children}
    </TranslationKeyContext.Provider>
  );
}; 